import { css } from '@emotion/react'
import { Button, HFlow, Icon } from 'bold-ui'
import { PageContent } from 'components/layout/PageContent'
import { PageLoading } from 'components/loading'
import { useEffect, useRef } from 'react'
import { useRouteMatch } from 'react-router'
import { useHistory, useLocation } from 'react-router-dom'
import {
  SistemaExternoIframe,
  sistemaExternoIframeStyles,
} from 'view/sistemas-externos/components/SistemaExternoIframe'
import { useLinkSistemaExterno } from 'view/sistemas-externos/components/useLinkSistemaExterno'

import { ProntuarioCard } from '../soap/aside/ProntuarioCard'
import { useSistemasExternosAtendimento } from './SistemasExternosAtendimentoContext'

interface SistemasExternosAtendimentoKeepAliveHostProps {
  path: string
}

interface UrlParams {
  iframeId?: string
}

interface LocationState {
  from?: string
}

export function SistemasExternosAtendimentoKeepAliveHost(
  props: Readonly<SistemasExternosAtendimentoKeepAliveHostProps>
) {
  const { path } = props
  const { sistemaExternoAtivo, resetSistemaExterno } = useSistemasExternosAtendimento()
  const { url, refresh, loading } = useLinkSistemaExterno(sistemaExternoAtivo, sistemaExternoAtivo?.cidadaoId)
  const match = useRouteMatch<UrlParams>({ path, exact: true })
  const history = useHistory()
  const location = useLocation<LocationState>()
  const containerRef = useRef<HTMLDivElement>(null)

  const isVisible = !!sistemaExternoAtivo && match?.params.iframeId === sistemaExternoAtivo.id

  useEffect(() => {
    const activeElement = document.activeElement
    if (!isVisible && activeElement instanceof HTMLElement && containerRef.current?.contains(activeElement)) {
      activeElement.blur()
    }
  }, [isVisible])

  if (!sistemaExternoAtivo) return null

  const handleVoltar = () => {
    resetSistemaExterno()

    if (location.state?.from) {
      history.push(location.state.from)
      return
    }

    const suffix = `/${sistemaExternoAtivo.id}`
    const parentUrl = location.pathname.endsWith(suffix)
      ? location.pathname.slice(0, location.pathname.length - suffix.length)
      : location.pathname
    history.push(parentUrl)
  }

  const renderTitle = () => (
    <HFlow hSpacing={0.5} alignItems='center'>
      <Button skin='ghost' size='small' onClick={handleVoltar} title='Voltar'>
        <Icon icon='angleLeft' size={1.2} />
      </Button>
      {sistemaExternoAtivo.nome}
    </HFlow>
  )

  const renderContent = () => (
    <Button
      size='small'
      skin='ghost'
      style={sistemaExternoIframeStyles.reloadButton}
      onClick={refresh}
      disabled={loading}
    >
      <Icon icon='undo' />
    </Button>
  )

  return (
    <div ref={containerRef} css={isVisible ? styles.visible : styles.hidden} aria-hidden={!isVisible}>
      <PageContent fluid type='filled'>
        <ProntuarioCard title={renderTitle()} content={renderContent()}>
          {url ? (
            <SistemaExternoIframe nome={sistemaExternoAtivo.nome} url={url} />
          ) : (
            <PageLoading message='Carregando sistema externo...' />
          )}
        </ProntuarioCard>
      </PageContent>
    </div>
  )
}

const styles = {
  visible: css`
    display: block;
  `,
  hidden: css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 0;
    overflow: hidden;
    visibility: hidden;
    pointer-events: none;
  `,
}
